<template>
  <div>
    <Container class="container-sm-none">
      <Progressbar :step="childrenWithIndexes.length > 0 ? 14 : 11" :step-count="model.stepCount">
        Sign the Application Form
      </Progressbar>
      <Card class="card-shadow">
        <div class="width-lg">
          <div class="pl-10 pl-md-0 pr-10 pr-md-0">
            <div class="flex justify-md-center align-center pb-10 pb-md-16">
              <h3 class="heading-3 weight-medium pr-4 text-left w-max">
                Select a Signature Method
                <Tooltip slot="tooltip">
                  <p class="text-left m-0">
                    
    Choose a <b>signature method</b> so that if more than one adult is being insured, each person can sign the application form for themselves.
    <br/>
    <br/>
    In the event of changing health insurance providers, the application form must be signed no later than 30 September.
  
                  </p>
                </Tooltip>
              </h3>
            </div>

            <InfoBox v-if="temporaryInfoVisible" class="mt-5 ib-warning" :icon="require('@/assets/svg/alert-icon.svg')">
              <p class="body-3 weight-regular mr-0 ml-0 mb-0 mt-0">
                <strong>Please note:</strong> Applications to change health insurance company from 1/1/2025 must be submitted no later than 30/9/2024. This is the last date when a signed application form can be sent by post or delivered to your nearest Union Health Insurance contact point. <br> <br> 

<strong>We therefore recommend signing the application form with a secure digital signature, which must be done no later than 30/9/2024 if you want to be insured with us from 1/1/2025.</strong>
              </p>
            </InfoBox>

            <InfoBox v-if="temporaryInfoAboutValidityVisible"  class="mt-5 ib-warning" :icon="require('@/assets/svg/alert-icon.svg')">
              <p class="body-3 weight-regular mr-0 ml-0 mb-0 mt-0">
                <strong>Please note:</strong> You are currently applying for a change of health insurance company with effect from 1/1/2026.
              </p>
            </InfoBox>

            <div class="input-box-holder" v-has-value="model.delivery" v-validate="errors">
              <div class="validation-output">
                <ErrorHandler class="text-left mt-10 error" id="V077">
                  You have not selected any of the options. To continue, please select one option.
                </ErrorHandler>
              </div>

              <EsignDeliveryComponent
                v-if="eSignatureDeliveryVisible"
                :model="model"
                :delivery="model.delivery"
                :option="DeliveryTypeEnum.E_SIGNATURE"
                :value="model.delivery.deliveryType"
                :errors="errors"
                @input="toggleDeliveryType(DeliveryTypeEnum.E_SIGNATURE)"
              />

              <RadioButton
                v-if="eIDDeliveryVisible"
                @input="toggleDeliveryType(DeliveryTypeEnum.EID)"
                :value="model.delivery.deliveryType"
                :option="DeliveryTypeEnum.EID"
                class="rd-card mt-10 heading-4 weight-medium"
                title='Qualified Electronic Signature (eID)'
                with-badge
              >
                <template v-slot:info>
                  <p class="body-3 mt-5">
                    You can sign the application form online using a link with a card reader connected to your computer.
                  </p>
                </template>
              </RadioButton>

              <CourierDeliveryComponent
                v-if="courierVisible"
                :model="model"
                :errors="errors"
                :delivery="model.delivery"
                :option="DeliveryTypeEnum.COURIER"
                :value="model.delivery.deliveryType"
                @input="toggleDeliveryType(DeliveryTypeEnum.COURIER)"
                :selected="model.delivery.deliveryType === DeliveryTypeEnum.COURIER"
              />

              <RadioButton
                v-if="!postDisable"
                @input="toggleDeliveryType(DeliveryTypeEnum.POST)"
                :value="model.delivery.deliveryType"
                :option="DeliveryTypeEnum.POST"
                :checked="model.delivery.deliveryType === DeliveryTypeEnum.POST"
                class="rd-card mt-10 heading-4 weight-medium"
                title='By Post'
              >
                <template v-slot:info>
                  <p class="body-3 mt-5">
                    Sign and return the completed application form for free in the pre-printed envelope.
                  </p>
                </template>
                <template v-slot:default>
                  <div>
                    <InfoBox class="mt-5 ib-warning" :icon="require('@/assets/svg/alert-icon.svg')">
                      <p class="m-0 pr-md-5 weight-regular body-3">
                        
    <b>How does it work?</b>
    <ul class="p-0">
        <li>
            Wait to receive for your completed application form and free return envelope by post.
        </li>
        <li>
            Sign the completed application form and return it to us for free in the envelope provided, <b>along with a copy of your identity document</b>, in accordance with new legislation.
        </li>
    </ul>
  
                      </p>
                    </InfoBox>
                  </div>
                </template>
              </RadioButton>

              <RadioButton
                v-if="!printDisable"
                @input="toggleDeliveryType(DeliveryTypeEnum.PRINT)"
                :value="model.delivery.deliveryType"
                :option="DeliveryTypeEnum.PRINT"
                :checked="model.delivery.deliveryType === DeliveryTypeEnum.PRINT"
                class="rd-card mt-10 heading-4 weight-medium"
                title='Print and Return'
              >
                <template v-slot:info>
                  <p class="body-3 mt-5">
                    You can print out the completed application form, sign it and post it back to us.
                  </p>
                </template>
                <template v-slot:default>
                  <div>
                    <InfoBox class="mt-5 ib-warning" :icon="require('@/assets/svg/alert-icon.svg')">
                      <p class="m-0 pr-md-5 weight-regular body-3">
                        
    <b>How does it work?</b>
    <ul class="p-0">
        <li>
            <b>Download, print and sign the completed application form.</b>
        </li>
        <li>
            Please post <b>the signed application form</b> in accordance with new legislation <b>along with a copy of your identity document</b> to:
            <br>Union zdravotná poisťovňa a. s.
            <br>Karadžičova 10
            <br>814 53  Bratislava
        </li>
        <li>
            <b>We will notify you by e-mail once your application has been received and processed.</b>
        </li>
    </ul>
  
                      </p>
                      <div v-if="temporaryInfoAboutDeliveryPostVisible">
                        <br/>
                        <b>
                          The last day to apply for changing health insurance providers via a post office is 30.09.2023.
                        </b>
                      </div>
                    </InfoBox>
                  </div>
                </template>
              </RadioButton>

              <ContactPlaceDeliveryComponent
                v-if="!contactPlaceDisable"
                :model="model"
                :delivery="model.delivery"
                :option="DeliveryTypeEnum.CONTACT_PLACE"
                :value="model.delivery.deliveryType"
                @input="toggleDeliveryType(DeliveryTypeEnum.CONTACT_PLACE)"
                :selected="model.delivery.deliveryType === DeliveryTypeEnum.CONTACT_PLACE"
                :errors="errors"
              />
            </div>

            <ButtonGroup v-if="!isSignUpdateRoute" class="pt-20">
              <Button
                class="btn-outline-tertiary w-full"
                :icon-left="require('@/assets/svg/icon-left-gray.svg')"
                @click="redirectBack"
              >
                Back
              </Button>
              <Button
                class="btn-primary w-full"
                :icon-right="require('@/assets/svg/icon-right-white.svg')"
                @click="nextStep"
                v-scroll-to-error
              >
                Continue
              </Button>
            </ButtonGroup>

            <ButtonGroup v-if="isSignUpdateRoute" class="pt-20">
              <Button
                class="btn-primary w-full"
                @click="nextStep"
                v-scroll-to-error
              >
                Change the Application Signature Method
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Card>
    </Container>
  </div>
</template>
<script>
import Container from '@/components/Container/Container.vue';
import Card from '@/components/Card/Card.vue';
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue';
import Button from '@/components/Button/Button.vue';
import RadioButton from '@/components/RadioButton/RadioButton.vue';
import routes from '@/mixins/routes';
import { COUNTRY_CODE_FOR_SLOVAKIA } from '@/config';
import persons from '@/mixins/persons';
import enums from '@/mixins/enums';
import lead from '@/mixins/lead';
import addressMixin from '@/mixins/address';
import distributionChannels from '@/mixins/distributionChannels';
import deliveryTypesVisibility from '@/mixins/deliveryTypesVisibility';
import attachmentValidations from '@/mixins/attachmentValidations';
import EsignDeliveryComponent from '@/pages/delivery-components/EsignDeliveryComponent.vue';
import save from '@/mixins/save';
import CourierDeliveryComponent from '@/pages/delivery-components/CourierDeliveryComponent.vue';
import ContactPlaceDeliveryComponent
from '@/pages/delivery-components/ContactPlaceDeliveryComponent.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import InfoBox from '@/components/InfoBox/InfoBox.vue';
import GA from '@/mixins/GA';
import Progressbar from '@/components/Progressbar/Progressbar.vue';

export default {
  name: 'SigningPage',
  components: {
    Progressbar,
    Tooltip,
    ContactPlaceDeliveryComponent,
    CourierDeliveryComponent,
    EsignDeliveryComponent,
    RadioButton,
    ButtonGroup,
    Card,
    Container,
    Button,
    ErrorHandler,
    InfoBox,
  },
  mixins: [routes, persons, enums, lead, addressMixin, distributionChannels, deliveryTypesVisibility, attachmentValidations, save, GA],
  props: {
    errors: { type: Array, required: true },
    model: { type: Object, required: true },
  },
  data() {
    return {
      triedGoToNextStep: false,
      userTriedToSave: false,
    };
  },
  created() {
    this.model.step = 20;
    if (this.model.lastStep < 20) this.model.lastStep = 20;
  },
  mounted() {
    if (this.isSignUpdateRoute) {
      this.$router.maxStep = 23;
      this.model.isUpdateDeliveryTypeRoute = true;
      const delivery = new this.$kotlin.model.Delivery_init();
      if (!this.model.isMobile) {
        delivery.eSignatureDevice = this.enums.DeviceEnum.ON_ANOTHER_DEVICE;
      }
      delivery.phone = this.model.delivery.phone;
      delivery.email = this.model.delivery.email;
      this.model.delivery = delivery;
    }
    this.funnelStep('Podpísanie prihlášky');
  },
  methods: {
    redirectBack() {
      this.$router.goToStep(this.getNearestPreviousAvailableStep());
    },
    /*
    * Returns nationality of person, if person is child then returns nationality
    * of first legal representative
    * */
    getNationality() {
      const insuredPerson = this.insuredPersons[0];
      return insuredPerson.isAdult
        ? insuredPerson.nationality
        : insuredPerson.firstLegalRepresentative.nationality;
    },
    getEvidenceType() {
      const insuredPerson = this.insuredPersons[0];
      return insuredPerson.isAdult ? insuredPerson.evidenceType : insuredPerson.firstLegalRepresentative.evidenceType;
    },
    /*
    * Method will toggle delivery type.
    * It used by other delivery types like POST, PRINT, CONTACT_PLACE
    * */
    toggleDeliveryType(type) {
      this.delivery.deliveryAddress = JSON.parse(JSON.stringify(this.uniqueSlovakAddressesWithOtherOption[0].name$));
      this.delivery.deliveryType = type === this.delivery.deliveryType ? null : type;
    },
    nextStep() {
      this.triedGoToNextStep = true;
      if (this.errors.length === 0 && this.model.delivery.deliveryType) {
        this.funnelInteraction();
        if (this.isVIP) {
          if (this.model.delivery.deliveryType === this.DeliveryTypeEnum.E_SIGNATURE) {
            this.save();
            return;
          }
        }
        this.save();
      }
    },
    async save() {
      this.userTriedToSave = true;
      if (this.errors.length === 0) {
        const thankYouType = await this.saveForm();
        this.$router.maxStep = 22;
        this.$router.push({ name: thankYouType, query: this.$route.query, params: this.$route.params });
      }
    },
  },
  computed: {
    isSignUpdateRoute() { return this.$route.name === 'SignUpdate'; },
    insuredPersons() { return this.model.insuredPersons.array_hd7ov6$_0; },
    temporaryInfoVisible() {
      const property = this.getParsedProperty('temporaryInfoAboutDeliveryType');
      return this.isNowInDateTimeRange(property) && this.oneOfPersonHasChangeApplication;
    },
    temporaryInfoAboutDeliveryPostVisible() {
      const property = this.getParsedProperty('temporaryInfoAboutDeliveryPost');
      return this.isNowInDateTimeRange(property) && this.oneOfPersonHasChangeApplication;
    },
    temporaryInfoAboutValidityVisible() {
      const property = this.getParsedProperty('temporaryInfoAboutValidity');
      return this.isNowInDateTimeRange(property) && this.oneOfPersonHasChangeApplication;
    },
    delivery() { return this.model.delivery; },
    DeviceEnum() { return this.enums.DeviceEnum; },
    DeliveryTypeEnum() { return this.enums.DeliveryTypeEnum; },
    EvidenceTypeEnum() { return this.enums.EvidenceTypeEnum; },
    firstLegalRepresentativeValid() {
      return this.insuredPersons.at(0).firstLegalRepresentative.evidenceType !== this.EvidenceTypeEnum.ZIADOST;
    },
    adultValid() {
      return this.insuredPersons.at(0).evidenceType !== this.EvidenceTypeEnum.ZIADOST;
    },
    eSignatureDeliveryVisible() {
      return this.countAllAdultPersons < 2 // eSign is not visible for 2 adult persons
        && ((this.childrenWithIndexes.length === 0 && this.adultValid)
          || (this.onlyChildrenAndMaxOneRepresentativeAsInsuredPersons && this.representativesOnlyParents && this.firstLegalRepresentativeValid))
        && this.$store.state.eSignatureAttempts < 3 // User has only 3 attempts
        && ((this.getNationality()?.value === COUNTRY_CODE_FOR_SLOVAKIA && this.getEvidenceType() !== this.EvidenceTypeEnum.ZIADOST)
          || (this.getNationality()?.value !== COUNTRY_CODE_FOR_SLOVAKIA && this.getEvidenceType() === this.EvidenceTypeEnum.RESIDENCE_PERMIT))
        && this.$store.state.appConfiguration.showESign // eSign should be visible
        && (this.$store.state.appConfiguration.showESignWithID || this.isVIP) // if we don't want to options with ID then eSign is available only for VIP
        && !this.eSignatureDisable;// Is not disable by time from application properties
    },
    eIDDeliveryVisible() {
      return this.insuredPersons.length < 2
        && this.$store.state.eIDAttempts < 3
        && this.$store.state.appConfiguration.showEID
        && !this.eIDDisable;
    },
    courierVisible() {
      return !this.courierDisable && this.adults.length < 2;
    },
  },
};
</script>
